/* eslint-disable import/prefer-default-export */
import { debounce } from '@material-ui/core';
import { useField } from 'formik';
import * as React from 'react';
import { ChangeEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../forms.module.css';
import { Label } from '../Label';

interface EmailProps {
    label: string;
    id: string;
    name: string;
    required?: boolean;
    hideLabel?: boolean;
    fullWidth?: boolean;
    placeholder?: string;
    debounceOnChange?: number;
    style?: React.CSSProperties;
    hideError?: boolean;
    onBlur?: (event: React.FormEvent<HTMLInputElement>) => void;
    onChange?: (event: React.FormEvent<HTMLInputElement>) => void;
}

export const Email: React.FC<EmailProps> = ({
    id,
    label,
    required = false,
    hideLabel,
    fullWidth = false,
    placeholder,
    debounceOnChange = 250,
    style = {},
    hideError,
    onBlur,
    onChange,
    ...props
}) => {
    const { t } = useTranslation(['common']);
    const [field, meta] = useField(props as any);

    const debouncedOnChange = React.useMemo(
        () => debounce((e: React.FormEvent<HTMLInputElement>) => (onChange ? onChange(e) : null), debounceOnChange),
        [],
    );

    const handleChange: ChangeEventHandler<HTMLInputElement> = React.useCallback(
        (e: React.FormEvent<HTMLInputElement>) => {
            field.onChange(e);

            debouncedOnChange(e);
        },
        [field],
    );

    const handleBlur: ChangeEventHandler<HTMLInputElement> = React.useCallback(
        (e: React.FormEvent<HTMLInputElement>) => {
            if (onBlur) onBlur(e);

            field.onBlur(e);
        },
        [field],
    );

    const inputPlaceholder = placeholder || label || field?.name || '';

    const formatPlaceholder = (): string => (required ? `${inputPlaceholder} (${t('common:required')})` : inputPlaceholder);

    return (
        <>
            <Label label={label} id={id} required={required} hideLabel={hideLabel} />
            <input
                {...field}
                className={styles.InputElement}
                placeholder={formatPlaceholder()}
                style={{
                    width: fullWidth ? '100%' : 'auto',
                    borderLeft: required && hideLabel ? '2px solid var(--color-DarkGrey)' : '',
                    ...style,
                }}
                onChange={handleChange}
                onBlur={handleBlur}
                type="email"
                data-qa={`form-input-email-${id}`}
            />
            {!hideError && meta.touched && meta.error && (
                <div className={styles.Error}>
                    <span>{meta.error}</span>
                </div>
            )}
        </>
    );
};
